import { useState, useMemo } from "react";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Truck, PackageCheck, Plus, Warehouse, Printer, Phone, Mail, Clock, Boxes, ChevronRight, X } from "lucide-react";
import { DT } from "@/components/ui/denseTable";
import { ListPagination, usePager } from "@/components/ui/ListPagination";
import { SortHeader, useSort } from "@/components/ui/SortHeader";
import { ImportExportButtons } from "@/components/ImportExportButtons";

const API = process.env.REACT_APP_BACKEND_URL;
const GP_STYLE = { "With Vendor": "bg-amber-100 text-amber-700", Received: "bg-emerald-100 text-emerald-700", Cancelled: "bg-slate-100 text-slate-500" };

const daysBetween = (a, b) => Math.round((a - b) / 86400000);

// Roll every gate pass up into per-vendor stats used by the card grid + drawer.
function buildVendorStats(vendors, gatePasses) {
  const now = Date.now();
  const map = {};
  vendors.forEach((v) => {
    map[v.name] = {
      ...v,
      capabilities: (v.process || "").split(/[,/]/).map((s) => s.trim()).filter(Boolean),
      unitsInHand: 0, openPasses: [], receivedCount: 0, onTimeCount: 0, parts: {},
    };
  });
  gatePasses.forEach((g) => {
    const key = g.vendor_name;
    const v = map[key] || (map[key] = { name: key, capabilities: [], unitsInHand: 0, openPasses: [], receivedCount: 0, onTimeCount: 0, parts: {}, _orphan: true });
    const part = v.parts[g.product_name] || { name: g.product_name, jobs: 0, qty: 0, lead: g.turnaround_days || v.turnaround_days || 0 };
    part.jobs += 1; part.qty += Number(g.quantity) || 0;
    v.parts[g.product_name] = part;
    if (g.status === "With Vendor") {
      v.unitsInHand += Number(g.pending_qty ?? g.quantity) || 0;
      v.openPasses.push(g);
    } else if (g.status === "Received") {
      v.receivedCount += 1;
      if (g.received_at && g.expected_return && new Date(g.received_at) <= new Date(g.expected_return)) v.onTimeCount += 1;
    }
  });
  return Object.values(map).map((v) => ({
    ...v,
    partList: Object.values(v.parts).sort((a, b) => b.qty - a.qty),
    openPasses: v.openPasses.sort((a, b) => new Date(a.expected_return || 0) - new Date(b.expected_return || 0)),
    onTimePct: v.receivedCount ? Math.round((v.onTimeCount / v.receivedCount) * 100) : null,
    hasOverdue: v.openPasses.some((g) => g.expected_return && new Date(g.expected_return).getTime() < now),
  })).sort((a, b) => (b.unitsInHand - a.unitsInHand) || String(a.name).localeCompare(b.name));
}

// (OnTimeBar removed — on-time % renders inline in the dense vendor table.)

// Vendor master is rendered as a dense table (project rule R1); per-vendor details live in the drawer.

function VendorDrawer({ vendor, onClose, onReceive, canManage }) {
  if (!vendor) return null;
  const now = Date.now();
  return (
    <Sheet open={!!vendor} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto p-0" data-testid="osp-vendor-drawer">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <SheetTitle className="font-display flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${vendor.active !== false ? "bg-emerald-500" : "bg-slate-300"}`} />
            {vendor.name}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {vendor.capabilities.join(" · ") || "No capabilities listed"} · {vendor.turnaround_days || 0}d avg turnaround
          </SheetDescription>
          <div className="flex items-center gap-3 pt-1 text-[11px] text-slate-500">
            {vendor.phone && <a href={`tel:${vendor.phone}`} className="inline-flex items-center gap-1 hover:text-indigo-600"><Phone className="h-3 w-3" />{vendor.phone}</a>}
            {vendor.email && <a href={`mailto:${vendor.email}`} className="inline-flex items-center gap-1 hover:text-indigo-600"><Mail className="h-3 w-3" />{vendor.email}</a>}
          </div>
        </SheetHeader>

        {/* Scorecard */}
        <div className="px-5 pt-4 grid grid-cols-3 gap-2" data-testid="osp-drawer-scorecard">
          <div className="rounded-lg border border-slate-100 p-2.5 text-center">
            <div className="text-lg font-bold font-mono-data text-slate-800">{vendor.onTimePct == null ? "—" : `${vendor.onTimePct}%`}</div>
            <div className="text-[9px] uppercase tracking-wide text-slate-400">On-Time</div>
          </div>
          <div className="rounded-lg border border-slate-100 p-2.5 text-center">
            <div className="text-lg font-bold font-mono-data text-indigo-700">{(vendor.unitsInHand || 0).toLocaleString("en-IN")}</div>
            <div className="text-[9px] uppercase tracking-wide text-slate-400">Units In Hand</div>
          </div>
          <div className="rounded-lg border border-slate-100 p-2.5 text-center">
            <div className="text-lg font-bold font-mono-data text-slate-800">{vendor.openPasses.length}</div>
            <div className="text-[9px] uppercase tracking-wide text-slate-400">Open Jobs</div>
          </div>
        </div>

        {/* Material list */}
        <div className="px-5 py-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><Boxes className="h-3.5 w-3.5 text-indigo-500" /> Parts Handled</h4>
          {vendor.partList.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No parts routed to this vendor yet.</p>
          ) : (
            <div className="rounded-lg border border-slate-100 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-400">
                  <tr><th className="text-left px-3 py-2 font-semibold">Part</th><th className="text-right px-3 py-2 font-semibold">Jobs</th><th className="text-right px-3 py-2 font-semibold">Qty</th><th className="text-right px-3 py-2 font-semibold">Lead</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {vendor.partList.map((p) => (
                    <tr key={p.name} data-testid={`osp-part-${p.name}`}>
                      <td className="px-3 py-2 text-slate-800">{p.name}</td>
                      <td className="px-3 py-2 text-right font-mono-data text-slate-500">{p.jobs}</td>
                      <td className="px-3 py-2 text-right font-mono-data text-slate-700">{p.qty.toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2 text-right font-mono-data text-slate-500">{p.lead || 0}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Active jobs */}
        <div className="px-5 pb-6">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-amber-500" /> Active jobs ({vendor.openPasses.length})</h4>
          {vendor.openPasses.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Nothing currently out with this vendor.</p>
          ) : (
            <div className="space-y-2">
              {vendor.openPasses.map((g) => {
                const exp = g.expected_return ? new Date(g.expected_return).getTime() : null;
                const days = exp != null ? daysBetween(exp, now) : null;
                const late = days != null && days < 0;
                return (
                  <div key={g.id} className="rounded-lg border border-slate-100 p-3 flex items-center gap-3" data-testid={`osp-job-${g.gp_number}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-800 truncate">{g.product_name} <span className="font-mono-data text-slate-400 text-xs">× {Number(g.pending_qty ?? g.quantity).toLocaleString("en-IN")} pending</span></p>
                      <p className="text-[11px] text-slate-400 font-mono-data">{g.gp_number} · {g.order_code}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`text-[11px] font-semibold ${late ? "text-red-600" : days <= 1 ? "text-amber-600" : "text-slate-600"}`}>
                        {days == null ? "—" : late ? `${Math.abs(days)}d late` : days === 0 ? "due today" : `${days}d left`}
                      </span>
                      {canManage && (
                        <button onClick={() => onReceive(g.id)} className="block mt-1 text-[11px] text-emerald-600 hover:underline" data-testid={`osp-drawer-receive-${g.gp_number}`}>Receive All</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function VendorForm({ onCreate }) {
  const [f, setF] = useState({ name: "", process: "", turnaround_days: "3", phone: "", email: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onCreate({ ...f, turnaround_days: Number(f.turnaround_days) || 0 });
      setF({ name: "", process: "", turnaround_days: "3", phone: "", email: "" });
    } catch (err) { logger.error("OSP vendor create failed (toast shown by caller):", err); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-end" data-testid="osp-vendor-form">
      <div className="space-y-1"><Label className="text-[11px]">Vendor Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required className="h-8 text-xs" data-testid="osp-vendor-name" /></div>
      <div className="space-y-1 sm:col-span-2"><Label className="text-[11px]">Capabilities (Comma-Separated)</Label><Input value={f.process} onChange={(e) => setF({ ...f, process: e.target.value })} placeholder="Machining, Drilling" className="h-8 text-xs" data-testid="osp-vendor-process" /></div>
      <div className="space-y-1"><Label className="text-[11px]">Turnaround (Days)</Label><Input type="number" min="0" value={f.turnaround_days} onChange={(e) => setF({ ...f, turnaround_days: e.target.value })} className="h-8 text-xs" data-testid="osp-vendor-turnaround" /></div>
      <div className="space-y-1"><Label className="text-[11px]">Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="h-8 text-xs" /></div>
      <Button type="submit" disabled={saving} className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700" data-testid="osp-vendor-add"><Plus className="h-3 w-3 mr-1" />{saving ? "Adding…" : "Add Vendor"}</Button>
    </form>
  );
}

export default function OspPanel({ vendors = [], gatePasses = [], canManage, onCreateVendor, onReceive, onImported }) {
  const [drawerVendor, setDrawerVendor] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const stats = useMemo(() => buildVendorStats(vendors, gatePasses), [vendors, gatePasses]);
  const { sort, toggle, apply } = useSort();
  const { pageItems, pagerProps } = usePager(apply(stats, {
    name: (v) => v.name, units: (v) => v.unitsInHand || 0, onTime: (v) => v.onTimePct ?? -1, turnaround: (v) => v.turnaround_days || 0,
  }));
  const openCount = gatePasses.filter((g) => g.status === "With Vendor").length;

  return (
    <div className="space-y-6" data-testid="osp-panel">
      {/* Vendor Master — card grid */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2"><Warehouse className="h-4 w-4 text-indigo-600" /> OSP Vendor Master</h3>
            <p className="text-xs text-slate-400">{stats.length} vendor(s) · {openCount} job(s) currently out. Click a row for parts, lead times &amp; live jobs.</p>
          </div>
          {canManage && (
            <div className="flex items-center gap-1.5">
              <ImportExportButtons entity="osp_vendors" label="vendors" onImported={onImported} />
              <Button size="sm" className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowForm((s) => !s)} data-testid="osp-toggle-form">
                {showForm ? <X className="h-3 w-3 mr-1" /> : <Plus className="h-3 w-3 mr-1" />}{showForm ? "Close" : "Add Vendor"}
              </Button>
            </div>
          )}
        </div>

        {canManage && showForm && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-3">
            <VendorForm onCreate={async (v) => { await onCreateVendor(v); setShowForm(false); }} />
          </div>
        )}

        {stats.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-400">No OSP vendors yet — add one to get started.</div>
        ) : (
          <div className={DT.wrap} data-testid="osp-vendor-grid">
            <table className={`${DT.table} min-w-[900px]`}>
              <thead className={DT.thead}>
                <tr className={DT.headerRow}>
                  <SortHeader label="Vendor" k="name" sort={sort} onSort={toggle} cls={`${DT.th} ${DT.stickyLeft}`} />
                  <th className={DT.th}>Capabilities</th>
                  <SortHeader label="Units in Hand" k="units" sort={sort} onSort={toggle} align="right" cls={DT.thRight} />
                  <SortHeader label="On-Time" k="onTime" sort={sort} onSort={toggle} align="right" cls={DT.thRight} />
                  <SortHeader label="Turnaround" k="turnaround" sort={sort} onSort={toggle} align="right" cls={DT.thRight} />
                  <th className={DT.thCenter}>Status</th>
                  <th className={`${DT.thRight} ${DT.stickyRight}`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((v) => {
                  const active = v.active !== false;
                  return (
                    <tr key={v.name} className={`${DT.row} cursor-pointer`} onClick={() => setDrawerVendor(v)} data-testid={`osp-vendor-card-${v.name}`}>
                      <td className={`${DT.td} ${DT.stickyLeft}`}>
                        <span className="flex items-center gap-1.5 font-semibold text-slate-900">
                          <span className={`h-2 w-2 rounded-full shrink-0 ${active ? "bg-emerald-500" : "bg-slate-300"}`} />
                          {v.name}
                        </span>
                      </td>
                      <td className={DT.td}>
                        <div className="flex flex-wrap gap-1 max-w-[260px]">
                          {v.capabilities.length ? v.capabilities.map((c) => (
                            <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">{c}</span>
                          )) : <span className="text-[10px] text-slate-300 italic">No capabilities listed</span>}
                        </div>
                      </td>
                      <td className={DT.tdRight}>
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded ${v.unitsInHand > 0 ? "bg-indigo-50 text-indigo-700" : "bg-slate-50 text-slate-400"}`} data-testid={`osp-units-${v.name}`}>
                          <Boxes className="h-3 w-3" /> {v.unitsInHand.toLocaleString("en-IN")}
                        </span>
                        {v.hasOverdue && <span className="block text-[10px] font-semibold text-red-600 mt-0.5">overdue job</span>}
                      </td>
                      <td className={DT.tdRight}>{v.onTimePct == null ? <span className="text-[10px] text-slate-400 italic">No history</span> : <span className={`font-mono-data font-semibold ${v.onTimePct >= 90 ? "text-emerald-600" : v.onTimePct >= 75 ? "text-amber-600" : "text-red-600"}`}>{v.onTimePct}%</span>}</td>
                      <td className={DT.tdRight}>{v.turnaround_days || 0}d</td>
                      <td className={`${DT.td} text-center`}><span className={`text-[10px] font-mono-data uppercase px-2 py-0.5 rounded ${active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{active ? "Active" : "Inactive"}</span></td>
                      <td className={`${DT.td} ${DT.stickyRight}`}>
                        <div className={DT.actionsCell}>
                          <a href={v.phone ? `tel:${v.phone}` : undefined} onClick={(e) => e.stopPropagation()} aria-disabled={!v.phone}
                             className={`${DT.iconBtn} ${v.phone ? "" : "text-slate-300 pointer-events-none"}`} title={v.phone ? `Call ${v.phone}` : "No phone"}
                             data-testid={`osp-call-${v.name}`}>
                            <Phone className="h-3.5 w-3.5" />
                          </a>
                          <a href={v.email ? `mailto:${v.email}` : undefined} onClick={(e) => e.stopPropagation()} aria-disabled={!v.email}
                             className={`${DT.iconBtn} ${v.email ? "" : "text-slate-300 pointer-events-none"}`} title={v.email ? `Email ${v.email}` : "No email"}
                             data-testid={`osp-email-${v.name}`}>
                            <Mail className="h-3.5 w-3.5" />
                          </a>
                          <button type="button" className={DT.iconBtn} title="Open vendor details" data-testid={`osp-view-${v.name}`}>
                            <ChevronRight className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <ListPagination {...pagerProps} testid="osp-pagination" />
          </div>
        )}
      </div>

      {/* Gate passes */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2"><Truck className="h-4 w-4 text-amber-600" /> Outward Gate Passes</h3>
        </div>
        <div className="overflow-x-auto">
          <table className={DT.table}>
            <thead className={DT.thead}>
              <tr className={DT.headerRow}>
                {["Gate Pass", "Order / Stage", "Product", "Vendor / Process", "Sent Qty", "Received", "Sent", "Expected", "Status", "Actions"].map((h) => <th key={h} className={DT.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {gatePasses.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">No gate passes yet — ship an OSP stage from the Schedule tab.</td></tr>}
              {gatePasses.map((g) => (
                <tr key={g.id} className={DT.row} data-testid={`gate-pass-${g.gp_number}`}>
                  <td className="px-4 py-3 font-mono-data text-xs font-semibold text-slate-700">{g.gp_number}</td>
                  <td className="px-4 py-3 font-mono-data text-xs text-slate-500">{g.order_code}<span className="block text-[10px] text-slate-400 font-sans">{g.stage_name || "—"}</span></td>
                  <td className="px-4 py-3 text-slate-800">{g.product_name}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{g.vendor_name}<span className="text-slate-400"> · {g.process || "—"}</span></td>
                  <td className="px-4 py-3 font-mono-data text-slate-700">{Number(g.quantity).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 font-mono-data text-xs" data-testid={`gp-received-${g.gp_number}`}>
                    <span className="text-emerald-700 font-semibold">{Number(g.received_qty || 0).toLocaleString("en-IN")}</span>
                    {Number(g.pending_qty) > 0 && <span className="block text-[10px] text-orange-600">{Number(g.pending_qty).toLocaleString("en-IN")} pending</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{g.sent_at ? new Date(g.sent_at).toLocaleDateString() : "—"}</td>
                  <td className={`px-4 py-3 text-xs ${g.overdue ? "text-red-600 font-semibold" : "text-slate-500"}`}>{g.expected_return ? new Date(g.expected_return).toLocaleDateString() : "—"}{g.overdue ? " (late)" : ""}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-mono-data uppercase px-2 py-1 rounded ${GP_STYLE[g.status] || "bg-slate-100 text-slate-600"}`}>{g.status === "With Vendor" && Number(g.received_qty) > 0 ? "Partially Received" : g.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <a href={`${API}/api/osp/gate-passes/${g.id}/pdf`} target="_blank" rel="noopener noreferrer" className="h-8 px-2 rounded-md inline-flex items-center gap-1 text-slate-600 hover:bg-slate-100 text-[11px] font-medium" data-testid={`print-gp-${g.gp_number}`} title="Print / download gate pass">
                        <Printer className="h-3.5 w-3.5" /> Print
                      </a>
                      {canManage && g.status === "With Vendor" && (
                        <button onClick={() => onReceive(g.id)} className="h-8 px-2 rounded-md inline-flex items-center gap-1 text-emerald-600 hover:bg-emerald-100 text-[11px] font-medium" data-testid={`receive-gp-${g.gp_number}`} title="Receive everything still pending on this gate pass">
                          <PackageCheck className="h-3.5 w-3.5" /> Receive All
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <VendorDrawer vendor={drawerVendor} onClose={() => setDrawerVendor(null)} onReceive={(id) => { onReceive(id); setDrawerVendor(null); }} canManage={canManage} />
    </div>
  );
}
